/* eslint-disable no-param-reassign */
import _ from 'underscore';
import { localizeSegmentationValue, segmentationType } from '@bingads-webui-campaign/segmentation-utilities';
import { merge } from '@bingads-webui-universal/primitive-utilities';

import tooltipTemplateVNext from './tooltip-template-vnext.pug';

import {
  CHART_TYPES,
} from './consts';
import {
  formatMetric,
  isMetricPercentage,
  localizeMetric,
  localizeTitle,
} from './metrics';

const CHART_TYPE = {
  COLUMN: 'column',
  LINE: 'line',
};

const VNextColors = [
  '#0078d4',
  '#022f22',
  '#e3008c',
  '#4b003f',
  '#917edb',
  // above 5 colors are revised against default colors for
  // competition card which has at most 5 lines,
  // work with designer to get below 5 updated if necessary
  '#01188F',
  '#006D21',
  '#0C8484',
  '#D8D8D8',
  '#666666',
];

const VNextRecommendationCardColors = [
  // From Figma:
  // https://www.figma.com/file/UiSbekGiA9XqEL6WM2v5allf/Microsoft-Advertising-HIG-0.01.01?node-id=11345%3A12550
  '#0078d4',
  '#022F22',
  '#E3008C',
  '#00188F',
  '#E86E58',
  '#001D3F',
  '#917EDB',
  '#502006',
  '#00A5B0',
  '#4B003F',
  '#D9D9D9',
];

function getChartType(primarySegmentType = null) {
  return _.contains([
    segmentationType.DayOfWeek,
    segmentationType.HourOfDay,
    segmentationType.Device,
    null,
  ], primarySegmentType) ?
    CHART_TYPE.COLUMN :
    CHART_TYPE.LINE;
}

function showXLabels(primarySegmentType = null) {
  return _.contains([
    segmentationType.DayOfWeek,
    segmentationType.HourOfDay,
    segmentationType.Device,
  ], primarySegmentType);
}

function setSeriesVNextMarker(chartOptions, subType) {
  const vNextMarkerSymbols = [
    'circle',
    'diamond',
    'triangle-down',
    'triangle',
    'square',
    'circle',
    'diamond',
    'triangle-down',
    'triangle',
    'square',
  ];
  _.each(chartOptions.series, (sery, index) => {
    if (index < vNextMarkerSymbols.length) {
      sery.marker = {
        symbol: vNextMarkerSymbols[index],
        enabled: subType !== 'Line_Weekly_Chart',
        lineWidth: 2,
        radius: 6,
      };
    }
    sery.lineWidth = 2;
  });
}

function getYAxis(i18n, metric) {
  return {
    title: {
      text: null,
      style: {
        color: '#333333',
        fontFamily: 'Segoe UI',
        fontWeight: 'normal',
        fontSize: '12px',
      },
    },
    labels: {
      formatter() {
        return `${formatMetric(i18n, metric, this.value, true)}`;
      },
    },
    min: 0,
    ceiling: isMetricPercentage(metric) ?
      1 : null,
    minTickInterval: isMetricPercentage(metric) ?
      0.01 : 1,
    tickPixelInterval: 30,
    gridLineDashStyle: 'dash',
    visible: true,
    showFirstLabel: true,
    showLastLabel: true,
  };
}

function defaultChartOptions({
  i18n,
  metric,
  primarySegmentType,
  series,
  localizeCategory,
}) {
  return {
    series,
    tooltip: {
      useHTML: true,
      formatter({ chart }) {
        const options = {
          color: this.color,
          blockChart: chart.options.chart.type === CHART_TYPE.COLUMN,
          segment: this.key === 0 ? null : localizeCategory(this.key),
          name: this.series.options.fullName || this.series.name,
          metric: localizeMetric(i18n, this.series.options.metric || metric),
          metricValue: formatMetric(i18n, this.series.options.metric || metric, this.y),
          index: this.series.index,
        };
        return tooltipTemplateVNext(options);
      },
    },
    chart: {
      type: getChartType(primarySegmentType),
      spacingTop: 10,
      spacingLeft: 0,
      spacingRight: 0,
      spacingBottom: 10,
      marginLeft: 50,
      marginRight: 10,
    },
    colors: [
      '#006464',
      '#BE5A00',
      '#B40091',
      '#600090',
      '#0078D7',
      '#01188F',
      '#006D21',
      '#0C8484',
      '#D8D8D8',
      '#666666',
    ],
    credits: {
      enabled: false,
    },
    legend: {
      align: 'right',
      verticalAlign: 'top',
      layout: 'horizontal',
      width: 350,
      itemStyle: {
        fontWeight: 'normal',
        textOverflow: 'ellipsis',
        overflow: 'hidden',
      },
      squareSymbol: false,
      symbolRadius: 0,
      symbolHeight: 5,
      symbolWidth: 19,
    },
    yAxis: getYAxis(i18n, metric),
    xAxis: {
      visible: true,
      lineWidth: 1,
      lineColor: '#DEE0E3',
      labels: {
        enabled: showXLabels(primarySegmentType),
        formatter() {
          return `${localizeCategory(this.value)}`;
        },
        style: {
          fontFamily: 'Segoe UI',
        },
      },
      tickLength: 0,
      type: 'category',
      plotBands: [],
    },
    title: {
      text: null,
    },
    plotOptions: {
      series: {
        maxPointWidth: 75,
        connectNulls: true,
        zoneAxis: 'x',
      },
      line: {
        pointPlacement: 'on',
      },
    },
  };
}

const primaryChartOptions = config => merge(config, {
  chart: {
    height: 300,
  },
});

const weeklyDataChartOptions = (i18n, config) => {
  const data = merge(config, {
    plotOptions: {
      line: {
        lineWidth: 1,
      },
      series: {
        marker: {
          enabled: false,
        },
      },
    },
  });

  const { xAxis } = data;
  delete data.xAxis;

  data.xAxis = [
    merge(xAxis, {
      showFirstLabel: false,
      tickLength: 5,
      tickInterval: 24,
      gridLineWidth: 1,
      labels: {
        enabled: true,
        formatter() {
          return i18n.formatDate(new Date(this.value), {
            skeleton: 'h',
            timeZone: null,
          });
        },
        style: {
          fontFamily: 'Segoe UI',
        },
      },
    }),
    {
      linkedTo: 0,
      opposite: true,
      type: 'category',
      showFirstLabel: true,
      tickLength: 0,
      tickInterval: 12,
      labels: {
        enabled: true,
        formatter() {
          // get category from first Axis
          const value = this.chart.xAxis[0].names[this.value];
          return i18n.formatDate(new Date(value), {
            skeleton: 'EEEEE',
            timeZone: null,
          });
        },
      },
      tickPositions: _.map(_.range(7), val => (val * 24) + 12),
    },
  ];
  // if (_.has(data, 'series')) {
  //   data.series = data.series.map((sery) => {
  //     sery = merge(sery, {
  //       marker: {
  //         enabled: false,
  //       },
  //     });
  //     return sery;
  //   });
  // }
  return data;
};

const lineColumnChartOptions = (i18n, config, metric) => {
  // Update ConversionRate series
  let data = config;
  const conversionSeries = _.findWhere(data.series, { yAxis: 1 });

  if (conversionSeries) {
    data = merge(config, {
      chart: {
      // need right margin for second yAxis
        marginRight: 70,
      },
      plotOptions: {
        column: {
          color: '#BBD5E6',
          maxPointWidth: 35,
        },
      },
    });

    const { yAxis } = data;
    delete data.yAxis;

    // Need to align yaxis of primary and secondary metrics.
    if (isMetricPercentage(metric)) {
      data.yAxis = [
        merge(yAxis, {
          tickInterval: 0.2,
          tickPixelInterval: 36,
        }),
        merge(getYAxis(i18n, 'ConversionRate'), {
          opposite: true,
          title: {
            text: localizeMetric(i18n, 'ConversionRate'),
          },
          tickInterval: 0.2,
          tickPixelInterval: 36,
        }),
      ];
    } else {
      data.yAxis = [
        yAxis,
        merge(getYAxis(i18n, 'ConversionRate'), {
          opposite: true,
          title: {
            text: localizeMetric(i18n, 'ConversionRate'),
          },
        }),
      ];
    }

    conversionSeries.type = 'column';
    conversionSeries.name = i18n.getString('Conversions_Data_Template', { name: conversionSeries.name });
  }

  if (config.chart.type === 'column') {
    data = merge(data, {
      data: {
        plotOptions: {
          series: {
            pointWidth: '16px',
          },
        },
      },
    });
  }

  return data;
};

const columnBottomChartOptions = (i18n, config, metric) => {
  const options = merge(config, {
    chart: {
      height: 250,
      marginTop: 40,
    },
    legend: {
      x: 30,
      y: 10,
      layout: 'horizontal',
      align: 'left',
      verticalAlign: 'bottom',
      itemStyle: {
        color: '#333333',
        fontFamily: 'Segoe UI',
        fontWeight: 'normal',
        fontSize: '10px',
        width: '200px',
      },
      width: 520,
    },
    title: {
      text: localizeTitle(i18n, metric),
      style: {
        color: '#333333',
        fontFamily: 'Segoe UI',
        fontWeight: 'normal',
        fontSize: '12px',
      },
    },
  });
  return options;
};

const recommendationCardChartOptions = (i18n, config, metric, subType) => {
  // configuration for all recommendation cards
  let data = merge(config, {
    chart: {
      height: 250,
      // Need extra space for yAxis title
      marginLeft: 70,
    },
    yAxis: {
      title: {
        text: localizeMetric(i18n, metric),
      },
    },
  });

  switch (subType) {
    case 'Line_Weekly_Chart':
      data = weeklyDataChartOptions(i18n, data);
      break;
    case 'Line_Column_Chart':
      data = lineColumnChartOptions(i18n, data, metric);
      break;
    case 'Search_Terms_Chart':
      data = columnBottomChartOptions(i18n, data, metric);
      break;
    default:
  }

  return data;
};

const secondaryChartOptions = (i18n, config, secondarySegment) => {
  const option = merge(config, {
    chart: {
      height: 142.5,
    },
    legend: {
      enabled: false,
      width: 0,
    },
    title: {
      text: localizeSegmentationValue({
        i18n,
        type: segmentationType.DeviceType,
        value: secondarySegment,
      }),
      margin: 0,
      floating: false,
      align: 'right',
      style: {
        fontSize: '12px',
      },
    },
  });
  return option;
};

const tileChartOptionsVNext = (config, isForRecommendationCard = false) => {
  const options = merge(config, {
    chart: {
      height: 180,
      marginRight: 10,
      marginBottom: 10,
    },
    colors: isForRecommendationCard ? VNextRecommendationCardColors : VNextColors,
    legend: {
      enabled: false,
    },
    xAxis: {
      labels: {
        enabled: false,
        style: {
          fontFamily: 'Segoe UI',
        },
      },
    },
    tickLength: 0,
    tooltip: {
      backgroundColor: null,
      borderWidth: 0,
      shadow: false,
      outside: false,
      positioner(labelWidth, labelHeight, point) {
        // a custom positioner to keep tooltips inside the plot area

        // if current point x + label width is bigger than plot width
        // which means part of label will be outside plot
        // so we put tooltip to the left of point
        // otherwise keep tooltip to the right
        // if corrected x is negative, make it zero
        // similar logic to handle y

        // we have predefined label width and height
        // in case label width and height is too small

        const predefinedLabelWidth = 190;
        labelWidth = Math.max(labelWidth, predefinedLabelWidth);
        let x = (point.plotX + labelWidth) > this.chart.plotWidth ?
          (point.plotX - labelWidth) :
          point.plotX;
        if (x < 0) {
          x = 0;
        }

        const predefinedLabelHeight = 110;
        labelHeight = Math.max(labelHeight, predefinedLabelHeight);
        let y = (point.plotY + labelHeight) > this.chart.plotHeight ?
          (point.plotY - labelHeight) :
          point.plotY;
        if (y < 0) {
          y = 0;
        }

        return { x, y };
      },
    },
  });
  return options;
};

export function getChartOptions({
  i18n,
  type,
  subType,
  metric,
  primarySegmentType,
  secondarySegment,
  series,
  localizeCategory,
}) {
  let chartOptions = defaultChartOptions({
    i18n,
    metric,
    primarySegmentType,
    series,
    localizeCategory,
  });

  // customize default chart options based on type
  switch (type) {
    case CHART_TYPES.LargeTile:
    case CHART_TYPES.SmallTile:
      tileChartOptionsVNext(chartOptions);
      setSeriesVNextMarker(chartOptions);
      break;
    case CHART_TYPES.Secondary:
      chartOptions = secondaryChartOptions(
        i18n,
        chartOptions,
        secondarySegment
      );
      tileChartOptionsVNext(chartOptions, true);
      setSeriesVNextMarker(chartOptions, subType);
      chartOptions.chart.height = 130;
      break;
    case CHART_TYPES.RecommendationCard:
      chartOptions = recommendationCardChartOptions(
        i18n,
        chartOptions,
        metric,
        subType
      );
      tileChartOptionsVNext(chartOptions, true);
      setSeriesVNextMarker(chartOptions, subType);
      break;
    default:
      chartOptions = primaryChartOptions(chartOptions);
      tileChartOptionsVNext(chartOptions, true);
      setSeriesVNextMarker(chartOptions, subType);
      chartOptions.chart.height = 277;
  }

  return chartOptions;
}
